// The owner-only permissions given to one person before roles held them (gap #18:
// permissions.listGrants; BL-137 gives no new ones), for the owner, above the people: each permission in
// words (its key on hover), who holds it and until when. Taking one back stays on the person's row; this
// is the overview. The same for roles limited to some places (roles.listScopes): who, which role, and
// where it reaches.

import { useEffect, useState } from 'react';
import { dateText } from '@flowology/kernel';
import { client } from '../api.ts';
import { scrollFocus } from '../shared/scrollFocus.ts';

export interface Grant {
  staffId: string;
  permission: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string | null;
}

/** "until Nov 1, 2026" or "no end date". */
export const untilText = (expiresAt: string | null, timeZone: string): string =>
  expiresAt ? `until ${dateText(expiresAt, timeZone)}` : 'no end date';

export function GivenPermissions({
  people,
  permissions,
  timeZone,
}: {
  /** The People list: names by staff id; a new list (after a grant changes) reads the grants again. */
  people: readonly { staffId: string; displayName: string }[];
  /** Owner-only permissions with their descriptions (roles.list). */
  permissions: readonly { key: string; description: string }[] | null;
  timeZone: string;
}) {
  const [grants, setGrants] = useState<Grant[] | null>(null);
  useEffect(() => {
    void client
      .call<{ grants: Grant[] }>('permissions.listGrants', {})
      .then((r) => setGrants(r.grants))
      .catch(() => setGrants([]));
  }, [people]);
  if (!grants?.length) return null;
  const who = (id: string) => people.find((p) => p.staffId === id)?.displayName ?? 'Someone';
  const what = (key: string) => permissions?.find((p) => p.key === key)?.description ?? key;
  return (
    <section className="sheet card given" aria-label="Given permissions">
      <h2>Given before roles</h2>
      <p className="muted small">
        Owner-only permissions given to one person before roles held them. Each runs to its end date; take one
        back on their row. Nothing new is given this way: give them a role.
      </p>
      <div className="tablewrap" ref={scrollFocus}>
        <table className="t" aria-label="Given permissions">
          <thead>
            <tr>
              <th>Person</th>
              <th>Permission</th>
              <th>How long</th>
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => (
              <tr key={`${g.staffId}:${g.permission}`}>
                <td>{who(g.staffId)}</td>
                <td title={g.permission}>{what(g.permission)}</td>
                <td className="nowrap">{untilText(g.expiresAt, timeZone)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export interface RoleScope {
  staffId: string;
  roleKey: string;
  businessUnitIds: string[];
  locationIds: string[];
}

/** Where a limited role reaches: "Plumbing and Drains", "Plumbing, and 2 addresses", "1 address". */
export function reachText(s: RoleScope, unitName: (id: string) => string): string {
  const units = s.businessUnitIds.map(unitName);
  const unitsText =
    units.length > 2 ? `${units.slice(0, -1).join(', ')} and ${units.at(-1)}` : units.join(' and ');
  const n = s.locationIds.length;
  const places = n ? `${n} address${n === 1 ? '' : 'es'}` : '';
  return unitsText && places ? `${unitsText}, and ${places}` : unitsText || places;
}

export function LimitedRoles({
  people,
  roleNames,
  unitName,
}: {
  /** The People list: names by staff id; a new list (after a limit changes) reads the limits again. */
  people: readonly { staffId: string; displayName: string }[];
  roleNames: Record<string, string>;
  unitName: (id: string) => string;
}) {
  const [scopes, setScopes] = useState<RoleScope[] | null>(null);
  useEffect(() => {
    void client
      .call<{ scopes: RoleScope[] }>('roles.listScopes', {})
      .then((r) => setScopes(r.scopes))
      .catch(() => setScopes([]));
  }, [people]);
  if (!scopes?.length) return null;
  const who = (id: string) => people.find((p) => p.staffId === id)?.displayName ?? 'Someone';
  return (
    <section className="sheet card given" aria-label="Roles limited to some places">
      <h2>Roles limited to some places</h2>
      <p className="muted small">
        Each reaches only the jobs, visits and invoices there; every other role covers the whole company.
        Change one on the person’s row.
      </p>
      <div className="tablewrap" ref={scrollFocus}>
        <table className="t" aria-label="Roles limited to some places">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Reaches only</th>
            </tr>
          </thead>
          <tbody>
            {scopes.map((s) => (
              <tr key={`${s.staffId}:${s.roleKey}`}>
                <td>{who(s.staffId)}</td>
                <td>{roleNames[s.roleKey] ?? s.roleKey}</td>
                <td>{reachText(s, unitName)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Blueprint's Who may do what › the exceptions for named people (the setup audit): the owner-only
 * permissions given to one person before roles held them, and the roles limited to some places, beside
 * the roles they are exceptions to. Each is taken back or changed on the person's row under People.
 */
export function NamedExceptions({ timeZone }: { timeZone: string }) {
  const [people, setPeople] = useState<{ staffId: string; displayName: string }[] | null>(null);
  const [permissions, setPermissions] = useState<{ key: string; description: string }[] | null>(null);
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    void client
      .call<{ people: { staffId: string; displayName: string }[] }>('people.list', {})
      .then((r) => setPeople(r.people))
      .catch(() => setPeople([]));
    void client
      .call<{
        roles: { key: string; name: string }[];
        permissions: { key: string; description: string; ownerOnly?: boolean }[];
      }>('roles.list', {})
      .then((r) => {
        setRoleNames(Object.fromEntries(r.roles.map((x) => [x.key, x.name])));
        setPermissions(r.permissions.filter((p) => p.ownerOnly));
      })
      .catch(() => setPermissions([]));
    void client
      .call<{ businessUnits: { id: string; name: string }[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
  }, []);
  if (!people) return null;
  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? 'a department';
  return (
    <>
      <GivenPermissions people={people} permissions={permissions} timeZone={timeZone} />
      <LimitedRoles people={people} roleNames={roleNames} unitName={unitName} />
    </>
  );
}
